import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import type { Cycle, CycleCreateInput, CycleUpdateInput } from '@/lib/types';
import { Button, Field, Input, Modal } from '@/components/ui';

export interface CycleFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  cycle?: Cycle;
  onSaved: (cycle: Cycle) => void;
}

/** `cycle_name` is a TEXT column; the schema caps it at 10000. */
const NAME_MAX = 10000;

interface FormValues {
  cycleName: string;
  startDate: string;
  endDate: string;
}

function toFormValues(cycle?: Cycle): FormValues {
  return {
    cycleName: cycle?.cycleName ?? '',
    startDate: cycle?.startDate ?? '',
    endDate: cycle?.endDate ?? '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ **These are the keys the form SENDS**, which is what a 422's `path` names;
 * `toFormValues()` is keyed on exactly those, so deriving the list from it
 * keeps it from drifting away from the controls. The catalog write schemas are
 * `.strict()`, so anything outside it — `createdBy`, `modifiedBy`, a typo —
 * belongs to no control here.
 *
 * A path can be non-empty and still bind to nothing, and that case is the worse
 * one because it looks bindable: routed by path, the message is attached to an
 * input that does not exist, disappears from the banner, and still inflates the
 * "fields need attention" count.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

export function CycleFormModal({ open, onClose, cycle, onSaved }: CycleFormModalProps) {
  if (!open) return null;
  return <CycleForm key={cycle?.id ?? 'new'} onClose={onClose} cycle={cycle} onSaved={onSaved} />;
}

function CycleForm({
  onClose,
  cycle,
  onSaved,
}: {
  onClose: () => void;
  cycle?: Cycle;
  onSaved: (cycle: Cycle) => void;
}) {
  const formId = useId();
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = cycle !== undefined;

  const baseline = useMemo(() => toFormValues(cycle), [cycle]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);
  const isDirty = changedKeys.length > 0;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
    // The date-order rule is reported against endDate, so moving either date
    // should clear it — otherwise fixing startDate leaves a stale message.
    if (key === 'startDate') {
      setClientErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
      setServerErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
    }
  }

  /*
   * The server only enforces `startDate <= endDate` on a PATCH that carries
   * BOTH dates, so a patch moving one date alone can leave a cycle whose end
   * precedes its start. Whenever either date changed, both are sent — the pair
   * is a range, and validating half of it is worse than not validating it.
   */
  function buildPayload(): CycleCreateInput | CycleUpdateInput {
    if (cycle) {
      const payload: CycleUpdateInput = {};
      if (changedKeys.includes('cycleName')) payload.cycleName = values.cycleName.trim();
      if (changedKeys.includes('startDate') || changedKeys.includes('endDate')) {
        payload.startDate = values.startDate.trim();
        payload.endDate = values.endDate.trim();
      }
      return payload;
    }

    return {
      cycleName: values.cycleName.trim(),
      startDate: values.startDate.trim(),
      endDate: values.endDate.trim(),
    };
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    const name = values.cycleName.trim();
    const start = values.startDate.trim();
    const end = values.endDate.trim();

    if (name === '') found.cycleName = 'Cycle name is required.';
    else if (name.length > NAME_MAX) found.cycleName = `Must be ${NAME_MAX} characters or fewer.`;

    // Neither date is nullable server-side, so blanking one in edit mode is a
    // change the API cannot accept — say so here rather than at the round trip.
    for (const [key, value, label] of [
      ['startDate', start, 'Start date'],
      ['endDate', end, 'End date'],
    ] as const) {
      if (value === '') found[key] = `${label} is required.`;
      else if (!isRealDate(value)) found[key] = `${label} must be a real date in YYYY-MM-DD form.`;
    }

    // ISO dates compare correctly as plain strings — no Date needed, and none
    // wanted: constructing one here is how an end-of-cycle date loses a day.
    if (!found.startDate && !found.endDate && start > end) {
      found.endDate = 'End date must be on or after the start date.';
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = cycle
        ? await api.updateCycle(cycle.id, payload)
        : await api.createCycle(payload as CycleCreateInput);
      onSaved(result.data);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * Split against this form's OWN field list, never against the shape of
         * the path. `unroutable` is everything no input here can carry — the
         * EMPTY-path `.strict()` and "at least one field" refusals, plus any
         * path naming a key this form does not render. Rendering it at form
         * level is the only way it is seen at all, and it is never counted as
         * highlighted.
         */
        const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
        setServerErrors(fields);
        if (unroutable.length > 0) setFormError(unroutable.join(' '));
        else if (Object.keys(fields).length === 0) setFormError(caught.message);
      } else {
        setFormError('Could not reach the server. Check your connection.');
      }
      setSubmitting(false);
    }
  }

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit
    ? !isDirty
    : values.cycleName.trim() === '' || values.startDate === '' || values.endDate === '';

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={cycle ? `Edit ${cycle.cycleName}` : 'New cycle'}
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create cycle'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Cycle name" required error={errors.cycleName}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.cycleName}
                  maxLength={NAME_MAX}
                  invalid={Boolean(errors.cycleName)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('cycleName', event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Start date" required error={errors.startDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // A native date input emits YYYY-MM-DD built from local parts,
                // which is exactly the wire format — no Date, no drift.
                type="date"
                value={values.startDate}
                invalid={Boolean(errors.startDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('startDate', event.target.value)}
              />
            )}
          </Field>

          <Field label="End date" required error={errors.endDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                value={values.endDate}
                min={values.startDate || undefined}
                invalid={Boolean(errors.endDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('endDate', event.target.value)}
              />
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}
